import { all, call, select, takeEvery, put } from 'redux-saga/effects';
import { fetchEntity } from '@classpass/saga-tools';

import URLS from 'constants/urls';
import { waitForModal } from 'components/Modal';
import { createInfoBanner, createErrorBanner } from 'components/Banner';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import Segment from 'helpers/segment';
import { reportError } from 'helpers/error-logging';
import { isClientValidationError, isPermissionDenied } from 'helpers/proxy-api-errors';
import { practitioners } from 'store/saga/entities/bound';
import { selectActiveVenueId, selectRouteParams } from 'selectors/general';
import { goToTab, goToItem } from '../ManageServices.saga';
import { transformPractitionerData } from './Practitioners.helpers';
import { messages } from './Practitioners.messages';

export const CREATE_PRACTITIONER = 'CREATE_PRACTITIONER';
export const createPractitioner = (payload, formAsyncFns) => ({
  type: CREATE_PRACTITIONER,
  payload,
  formAsyncFns,
});

export const EDIT_PRACTITIONER = 'EDIT_PRACTITIONER';
export const editPractitioner = (data, formAsyncFns) => ({
  type: EDIT_PRACTITIONER,
  data,
  formAsyncFns,
});

export const DELETE_PRACTITIONER = 'DELETE_PRACTITIONER';
export const deletePractitioner = id => ({
  type: DELETE_PRACTITIONER,
  id,
});

export const FETCH_PRACTITIONERS = 'FETCH_PRACTITIONERS';
export const fetchPractitioners = () => ({ type: FETCH_PRACTITIONERS });

export function* doCreatePractitioner(client, action) {
  const { httpClient } = client;
  const { payload, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;

  try {
    const { venueId, tab } = yield select(selectRouteParams);
    const endpoint = URLS.manageServices.practitioners.create(venueId);

    const { data } = yield call([httpClient, httpClient.post], endpoint, transformPractitionerData(payload));
    const { id: itemId } = data;

    yield all([
      put(fetchPractitioners()),
      put(createInfoBanner(messages.success.practitionerCreated)),
      call(resolveSubmit),
      put(goToTab(tab)),
      put(goToItem(itemId)),
    ]);
    yield call(braze.trackEvent, BRAZE_CUSTOM_EVENTS.NEW_PRACTITIONER_ADDED);
    yield call(Segment.event, 'Practitioner Created', {
      'practitioner id': itemId,
      'venue id': venueId,
      'practitioner name': `${payload.firstName || ''} ${payload.lastName || ''}`.trim(),
      email: payload.email,
    });
  } catch (error) {
    if (isPermissionDenied(error)) {
      yield all([put(createErrorBanner(messages.error.notSupported)), call(rejectSubmit)]);
    } else if (isClientValidationError(error)) {
      yield all([put(createErrorBanner(messages.error.validationFailed)), call(rejectSubmit)]);
    } else {
      yield all([
        put(createErrorBanner(messages.error.problemWithRequest)),
        call(rejectSubmit),
        call(reportError, error),
      ]);
    }
  }
}

export function* doEditPractitioner(client, action) {
  const { data, formAsyncFns } = action;
  const { resolveSubmit, rejectSubmit } = formAsyncFns;

  try {
    const routeParams = yield select(selectRouteParams);
    const { venueId, itemId } = routeParams;
    yield call(fetchEntity, practitioners, [client, venueId, data]);
    yield all([
      put(fetchPractitioners()),
      put(createInfoBanner(messages.success.practitionerEdited)),
      call(resolveSubmit),
      put(goToItem(itemId)),
    ]);
  } catch (error) {
    if (isPermissionDenied(error)) {
      yield all([put(createErrorBanner(messages.error.notSupported)), call(rejectSubmit)]);
    } else if (isClientValidationError(error)) {
      yield all([put(createErrorBanner(messages.error.validationFailed)), call(rejectSubmit)]);
    } else {
      yield all([
        put(createErrorBanner(messages.error.problemWithRequest)),
        call(rejectSubmit),
        call(reportError, error),
      ]);
    }
  }
}

export function* doDeletePractitioner(client, action) {
  const { httpClient } = client;
  const { id } = action;

  try {
    const { venueId, tab } = yield select(selectRouteParams);
    const endpoint = URLS.manageServices.practitioners.delete(venueId, id);
    const confirmed = yield call(waitForModal, client, 'DeletePractitionerModal');

    if (confirmed) {
      yield call([httpClient, httpClient.delete], endpoint);
      yield all([
        put(fetchPractitioners()),
        put(createInfoBanner(messages.success.practitionerDeleted)),
        put(goToTab(tab)),
      ]);
    }
  } catch (error) {
    if (isPermissionDenied(error)) {
      yield put(createErrorBanner(messages.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* doFetchPractitioners(client) {
  try {
    const venueId = yield select(selectActiveVenueId);
    return yield call(fetchEntity, practitioners, [client, venueId]);
  } catch (error) {
    if (isPermissionDenied(error)) {
      yield put(createErrorBanner(messages.error.notSupported));
    } else {
      yield all([put(createErrorBanner(messages.error.problemWithRequest)), call(reportError, error)]);
    }
  }
}

export function* watchActions(client) {
  yield takeEvery(CREATE_PRACTITIONER, doCreatePractitioner, client);
  yield takeEvery(EDIT_PRACTITIONER, doEditPractitioner, client);
  yield takeEvery(DELETE_PRACTITIONER, doDeletePractitioner, client);
  yield takeEvery(FETCH_PRACTITIONERS, doFetchPractitioners, client);
}
